import { computed, ref, type Ref } from 'vue'
import { acceptHMRUpdate, defineStore } from 'pinia'
import type { LayoutConfig } from '@/misc/custom-types'
import UserObject from '@/objects/User'
import type IUser from '@/types/User'
import UserService from '@/services/UserService'

export const useMainStore = defineStore('main', () => {
  const user = ref<IUser>(new UserObject().values()) as Ref<IUser>
  const profilePicture = ref<string>('')
  const companyName = ref<string>('')
  const profileToolbarVisible = ref<boolean>(false)
  const languages = ref<{ id: string, type: string }[]>([])
  const primaryLanguage: Ref<string | null> = ref<string | null>(null)
  const selectedLanguage: Ref<string | null> = ref<string | null>(null)

  const loading = ref<boolean>(false)
  const canViewOther = ref<boolean>(false)
  const isSuperAdmin = ref<boolean>(false)
  const hideSubheader = ref<boolean>(false)

  const layout = ref<LayoutConfig>({
    activeMenuItem: null,
    title: 'Algemeen',
    menuItems: [],
  }) as Ref<LayoutConfig>

  const shouldHideSubheader = computed<boolean>(() => {
    if (!hideSubheader.value) return false

    return (document.documentElement.scrollHeight - 250) > window.innerHeight && window.scrollY > 150
  })

  const toggleProfileToolbar = () => {
    profileToolbarVisible.value = !profileToolbarVisible.value
  }

  const navigateToReleaseNotes = () => {
    window.location.href = '/release-notes'
  }

  const doLogout = async () => {
    await UserService.logout()
    resetPersistedState()

    window.location.href = '/'
  }

  const resetPersistedState = () => {
    user.value = new UserObject().values()
    profilePicture.value = ''
    companyName.value = ''
    primaryLanguage.value = null
    selectedLanguage.value = null
    languages.value = []
  }

  return {
    user,
    companyName,
    layout,
    canViewOther,
    isSuperAdmin,
    loading,
    profilePicture,
    profileToolbarVisible,
    hideSubheader,
    languages,
    primaryLanguage,
    selectedLanguage,

    // computed
    shouldHideSubheader,

    // methods
    doLogout,
    toggleProfileToolbar,
    navigateToReleaseNotes
  }
}, {
  persist: {
    storage: sessionStorage,
    paths: ['user', 'profilePicture', 'companyName', 'primaryLanguage', 'languages', 'selectedLanguage'],
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useMainStore, import.meta.hot))
}
